export interface ExploreChannelAction {
    name: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    /** The Connect use case this opens (`@connect/use-cases.ts`): DANI's plan
     * screen for it, the same as clicking it on the channel's own page. */
    useCaseId: string;
}

export interface ExploreChannel {
    key: string;
    name: string;
    /** Short label for tight spaces (the Synthesize channels picker's
     * chips) — quoted from the design source's own `short` field. */
    short: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    color: string;
    tagline: string;
    actions: ExploreChannelAction[];
}

/**
 * The ten consumer data channels shown in the Connect
 * hover peek. Ported directly from the design source of record
 * (`MFour Studio JTBD v15.dc.html`, `SIGNALS()`) — names, icons, colors,
 * taglines and action labels are all quoted from that file, not invented
 * here. Every action opens the matching Connect use case's plan screen
 * (`useCaseId`), where DANI asks its questions before it runs; the prompts
 * the design file listed live on as those use cases' templates. None of
 * them depend on a feature this repo doesn't have yet (there's no dedicated
 * Feasibility/Census-weighting tool here — "Check feasibility" and "Weight
 * to Census" are DANI questions, same as the rest).
 */
export const EXPLORE_CHANNELS: ExploreChannel[] = [
    {
        key: 'demographic',
        name: 'Demographics',
        short: 'Demos',
        icon: 'user-circle-2',
        color: '#7C3AED',
        tagline: 'Age, gender, ethnicity, geography, income, household and more.',
        actions: [
            {
                name: 'Build an audience',
                icon: 'users',
                useCaseId: 'demographics-build',
            },
            {
                name: 'Check feasibility',
                icon: 'compass',
                useCaseId: 'demographics-feasibility',
            },
            {
                name: 'Weight to Census',
                icon: 'scale',
                useCaseId: 'demographics-census',
            },
            {
                name: 'Profile a segment',
                icon: 'id-card',
                useCaseId: 'demographics-profile',
            },
        ],
    },
    {
        key: 'psychographic',
        name: 'Psychographics',
        short: 'Attitudes',
        icon: 'brain',
        color: '#7C3AED',
        tagline: 'Attitudes, opinions, and intent — recurring monthly waves.',
        actions: [
            {
                name: 'Profile attitudes',
                icon: 'brain',
                useCaseId: 'attitudes-profile',
            },
            {
                name: 'Lifestyle segments',
                icon: 'users',
                useCaseId: 'attitudes-segments',
            },
            {
                name: 'Motivations and barriers',
                icon: 'split',
                useCaseId: 'attitudes-motivations',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'attitudes-join-survey',
            },
        ],
    },
    {
        key: 'location',
        name: 'Location',
        short: 'Location',
        icon: 'map-pin',
        color: '#059669',
        tagline: 'Visits with entry/exit and dwell across 750,000+ tracked locations.',
        actions: [
            {
                name: 'Visit trends',
                icon: 'activity',
                useCaseId: 'location-track-visits',
            },
            {
                name: 'Map a trade area',
                icon: 'compass',
                useCaseId: 'location-map-trade-areas',
            },
            {
                name: 'Compare competitors',
                icon: 'eye',
                useCaseId: 'location-compare-competitors',
            },
            {
                name: 'Dwell and dayparts',
                icon: 'clock',
                useCaseId: 'location-dwell-time',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'location-join-survey',
            },
        ],
    },
    {
        key: 'app',
        name: 'App',
        short: 'App',
        icon: 'smartphone',
        color: '#0EA5A4',
        tagline: 'App usage — open/close events, session duration, and frequency.',
        actions: [
            {
                name: 'Compare app usage',
                icon: 'smartphone',
                useCaseId: 'app-benchmark',
            },
            {
                name: 'Share of attention',
                icon: 'pie-chart',
                useCaseId: 'app-attention',
            },
            {
                name: 'Session trends',
                icon: 'activity',
                useCaseId: 'app-sessions',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'app-join-survey',
            },
        ],
    },
    {
        key: 'web',
        name: 'Web',
        short: 'Web',
        icon: 'globe',
        color: '#0EA5A4',
        tagline: 'Mobile web clickstream — domains, referrers, and search terms.',
        actions: [
            {
                name: 'Trace the funnel',
                icon: 'globe',
                useCaseId: 'web-funnels',
            },
            {
                name: 'Search terms',
                icon: 'search',
                useCaseId: 'web-searches',
            },
            {
                name: 'Content consumption',
                icon: 'book-open',
                useCaseId: 'web-content',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'web-join-survey',
            },
        ],
    },
    {
        key: 'purchase',
        name: 'Purchase',
        short: 'Purchase',
        icon: 'receipt',
        color: '#E86A17',
        tagline: 'Transaction-level purchases via Gmail e-receipts and Amazon accounts.',
        actions: [
            {
                name: 'Analyze baskets',
                icon: 'shopping-cart',
                useCaseId: 'purchase-baskets',
            },
            {
                name: 'Find leakage',
                icon: 'split',
                useCaseId: 'purchase-leakage',
            },
            {
                name: 'Share of wallet',
                icon: 'pie-chart',
                useCaseId: 'purchase-wallet',
            },
            {
                name: 'Price paid analysis',
                icon: 'tag',
                useCaseId: 'purchase-price',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'purchase-join-survey',
            },
        ],
    },
    {
        key: 'chatgpt',
        name: 'Gen AI LLM: ChatGPT',
        short: 'ChatGPT',
        icon: 'sparkles',
        color: '#2563EB',
        tagline: 'Full-history ChatGPT conversations — prompts, responses, citations, and ads.',
        actions: [
            {
                name: 'What shoppers ask',
                icon: 'message-circle',
                useCaseId: 'chatgpt-intent',
            },
            {
                name: 'Who gets named first',
                icon: 'bot',
                useCaseId: 'chatgpt-rank',
            },
            {
                name: 'Mentions over time',
                icon: 'trending-up',
                useCaseId: 'chatgpt-mentions',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'gen-ai-chatgpt-join-survey',
            },
        ],
    },
    {
        key: 'social',
        name: 'Digital Ads',
        short: 'Ads',
        icon: 'megaphone',
        color: '#DB2777',
        tagline: 'Ad exposure across social, video, audio streaming, and AI platforms.',
        actions: [
            {
                name: 'Ad exposure by platform',
                icon: 'megaphone',
                useCaseId: 'ads-exposure',
            },
            {
                name: 'Creative reach',
                icon: 'eye',
                useCaseId: 'ads-creatives',
            },
            {
                name: 'Share of attention',
                icon: 'pie-chart',
                useCaseId: 'ads-attention',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'digital-ads-join-survey',
            },
        ],
    },
    {
        key: 'mobilemedia',
        name: 'Media',
        short: 'Media',
        icon: 'play-circle',
        color: '#0891B2',
        tagline:
            'In-app streaming inside YouTube, Netflix, Prime and Disney+ — series, ads, and watch time.',
        actions: [
            {
                name: 'Where they watch',
                icon: 'play-circle',
                useCaseId: 'media-viewing',
            },
            {
                name: 'Cross-platform reach',
                icon: 'layers',
                useCaseId: 'media-reach',
            },
            {
                name: 'Content affinity',
                icon: 'heart',
                useCaseId: 'media-affinity',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'media-join-survey',
            },
        ],
    },
    {
        key: 'audio',
        name: 'Audio',
        short: 'Audio',
        icon: 'headphones',
        color: '#DB2777',
        tagline:
            'In-app streaming inside Spotify, Pandora and iHeart — track or podcast, artist, and listen time.',
        actions: [
            {
                name: 'Share of ear',
                icon: 'headphones',
                useCaseId: 'audio-share-of-ear',
            },
            {
                name: 'Podcast affinity',
                icon: 'mic',
                useCaseId: 'audio-podcasts',
            },
            {
                name: 'Listening trends',
                icon: 'activity',
                useCaseId: 'audio-trends',
            },
            {
                name: 'Analyze with my survey',
                icon: 'clipboard-list',
                useCaseId: 'audio-join-survey',
            },
        ],
    },
];

export interface SynthesizeChannelChip {
    key: string;
    name: string;
    short: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    color: string;
}

/**
 * Surveys itself, ported from the design source's `SIGNALS()` (its own
 * first entry, `key: 'survey'`). Deliberately not part of `EXPLORE_CHANNELS`
 * — that list is "the ten consumer data channels" the Explore hover peek
 * advertises, and survey data isn't one of the ten — but the Synthesize
 * channels wizard's own step 1 (design source: `comboChannels`) is built
 * from every signal, survey included.
 */
export const SURVEYS_CHANNEL: SynthesizeChannelChip = {
    key: 'survey',
    name: 'Surveys',
    short: 'Surveys',
    icon: 'clipboard-list',
    color: '#1E70FF',
};

/** Step 1 of the Synthesize channels wizard: Surveys first, then the ten
 * consumer data channels — matches `comboChannels` in the design source. */
export const SYNTHESIZE_CHANNELS: SynthesizeChannelChip[] = [SURVEYS_CHANNEL, ...EXPLORE_CHANNELS];
